import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Plus,
  FilePlus2,
  Pencil,
  Copy,
  Trash2,
  Loader2,
  ClipboardList,
  Calendar,
  Clock,
  User2,
  LineChart,
  Users,
  Bed,
  ArrowDownAZ,
  ListFilter,
  CalendarClock,
  Droplet,
  Utensils,
} from "lucide-react";
import PatientPickerDialog from "@/components/PatientPickerDialog";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { computePriority, LEVELS, FILTER_OPTIONS } from "@/lib/priority";
import { computeShiftDuration } from "@/lib/duration";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function PriorityBadge({ priority }) {
  const cfg = LEVELS[priority.level];
  return (
    <div
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold"
      style={{ backgroundColor: cfg.bg, color: cfg.color }}
      title={priority.reasons.join(" - ")}
      data-testid={`priority-badge-${priority.level}`}
    >
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{ backgroundColor: cfg.dot }}
      />
      {cfg.label}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [programs, setPrograms] = useState(null);
  const [stats, setStats] = useState({ total: 0, mine: 0, limit: 100 });
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [filter, setFilter] = useState("all");
  const [nurseFilter, setNurseFilter] = useState("all");
  const [sortMode, setSortMode] = useState("default"); // "default" | "name" | "date"
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, sRes] = await Promise.all([api.get("/programs"), api.get("/stats")]);
      setPrograms(pRes.data);
      setStats(sRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error cargando datos");
      setPrograms([]);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Compute priority once per program list (memoized) - {id: {level, reasons}}
  const priorities = useMemo(() => {
    if (!programs) return {};
    const result = {};
    for (const p of programs) {
      result[p.id] = computePriority(p);
    }
    return result;
  }, [programs]);

  // Counts per level for the filter bar
  const counts = useMemo(() => {
    const c = { all: programs?.length || 0, red: 0, yellow: 0, green: 0, gray: 0 };
    Object.values(priorities).forEach((pr) => {
      c[pr.level] = (c[pr.level] || 0) + 1;
    });
    return c;
  }, [priorities, programs]);

  // Distinct nurse names that appear in programs (admin only filter)
  const nurseOptions = useMemo(() => {
    if (!programs) return [];
    const set = new Set();
    for (const p of programs) {
      const n = (p.owner_name || "").trim();
      if (n) set.add(n);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [programs]);

  // Filtered + sorted programs
  // - "all" nurses: priority semaforo order (red > yellow > gray > green)
  // - specific nurse: date desc (most recent first)
  const visiblePrograms = useMemo(() => {
    if (!programs) return null;
    const order = { red: 0, yellow: 1, gray: 2, green: 3 };
    let filtered = filter === "all"
      ? programs
      : programs.filter((p) => priorities[p.id]?.level === filter);
    if (nurseFilter !== "all") {
      filtered = filtered.filter((p) => (p.owner_name || "").trim() === nurseFilter);
    }
    const arr = [...filtered];
    if (sortMode === "name") {
      // Alphabetical by patient name (A-Z)
      arr.sort((a, b) => {
        const na = (a.shift?.patient_name || "").trim().toLocaleLowerCase("es");
        const nb = (b.shift?.patient_name || "").trim().toLocaleLowerCase("es");
        return na.localeCompare(nb, "es");
      });
    } else if (sortMode === "date" || nurseFilter !== "all") {
      // Most recent shift first; tie-break by updated_at
      arr.sort((a, b) => {
        const da = a.shift?.date || "";
        const db = b.shift?.date || "";
        if (da !== db) return db.localeCompare(da);
        const ta = a.shift?.start_time || "";
        const tb = b.shift?.start_time || "";
        if (ta !== tb) return tb.localeCompare(ta);
        return (b.updated_at || "").localeCompare(a.updated_at || "");
      });
    } else {
      arr.sort((a, b) => {
        const la = order[priorities[a.id]?.level] ?? 9;
        const lb = order[priorities[b.id]?.level] ?? 9;
        return la - lb;
      });
    }
    return arr;
  }, [programs, priorities, filter, nurseFilter, sortMode]);

  const handleCreate = async (patient) => {
    if (!patient) {
      setPickerOpen(true);
      return;
    }
    setCreating(true);
    setPickerOpen(false);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data } = await api.post("/programs", {
        shift: {
          patient_name: patient.name,
          date: today,
          start_time: "07:00",
          end_date: today,
          end_time: "15:00",
          nurses: [user.name],
        },
        interventions: [],
        closing: {},
      });
      nav(`/programs/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo crear el programa");
    } finally {
      setCreating(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const { data } = await api.post(`/programs/${id}/duplicate`);
      toast.success("Programa duplicado");
      nav(`/programs/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo duplicar");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/programs/${toDelete.id}`);
      toast.success("Programa eliminado");
      setToDelete(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AppHeader subtitle="Panel principal" />

      <main className="mx-auto max-w-3xl px-4 py-6">
        {/* Welcome + stats */}
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                {user?.role === "admin" ? "Administrador" : "Enfermera"}
              </p>
              <h1 className="mt-1 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                Hola, {user?.name}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                {user?.role === "admin"
                  ? "Estas viendo todos los programas del piloto."
                  : "Aqui veras tus programas de cuidados."}
              </p>
            </div>
            <div className="text-right">
              <div className="text-xs font-medium text-slate-500">Piloto</div>
              <div className="font-mono text-lg font-bold text-[#1D4ED8]" data-testid="dashboard-stats">
                {stats.total}/{stats.limit}
              </div>
            </div>
          </div>
        </div>

        {/* Admin quick navigation */}
        {user?.role === "admin" && (
          <div className="mb-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-5" data-testid="dashboard-admin-nav">
            <Button
              variant="outline"
              className="h-12 w-full rounded-xl border-[#1D4ED8] text-base font-semibold text-[#1D4ED8] hover:bg-[#EFF6FF]"
              onClick={() => nav("/pacientes")}
              data-testid="dashboard-patients-link"
            >
              <Users className="mr-2 h-4 w-4" />
              Pacientes
            </Button>
            <Button
              variant="outline"
              className="h-12 w-full rounded-xl border-[#1D4ED8] text-base font-semibold text-[#1D4ED8] hover:bg-[#EFF6FF]"
              onClick={() => nav("/analisis")}
              data-testid="dashboard-analysis-link"
            >
              <LineChart className="mr-2 h-4 w-4" />
              Analisis clinico
            </Button>
            <Button
              variant="outline"
              className="h-12 w-full rounded-xl border-cyan-400 text-base font-semibold text-cyan-700 hover:bg-cyan-50"
              onClick={() => nav("/dialisis")}
              data-testid="dashboard-dialysis-link"
            >
              <Droplet className="mr-2 h-4 w-4" />
              Dialisis 24h
            </Button>
            <Button
              variant="outline"
              className="h-12 w-full rounded-xl border-amber-400 text-base font-semibold text-amber-700 hover:bg-amber-50"
              onClick={() => nav("/gastrostomia")}
              data-testid="dashboard-peg-link"
            >
              <Utensils className="mr-2 h-4 w-4" />
              PEG 24h
            </Button>
            <Button
              variant="outline"
              className="h-12 w-full rounded-xl border-slate-300 text-base font-semibold"
              onClick={() => nav("/admin")}
              data-testid="dashboard-admin-link"
            >
              <Plus className="mr-2 h-4 w-4" />
              Enfermeras
            </Button>
          </div>
        )}

        {/* Create new */}
        <Button
          onClick={() => handleCreate()}
          disabled={creating || stats.total >= stats.limit}
          className="mb-5 h-14 w-full rounded-2xl bg-[#1D4ED8] text-base font-semibold shadow-md hover:bg-[#1E40AF]"
          data-testid="dashboard-create-btn"
        >
          {creating ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <FilePlus2 className="mr-2 h-5 w-5" />
              Nuevo programa de cuidados
            </>
          )}
        </Button>

        {/* Programs list */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-bold text-slate-900 sm:text-lg">
            {user?.role === "admin" ? "Todos los programas" : "Mis programas"}
          </h2>
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setSortMode((m) => (m === "date" ? "default" : "date"))}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-all ${
                sortMode === "date"
                  ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
              }`}
              data-testid="sort-by-date-btn"
              title={sortMode === "date" ? "Quitar orden por fecha" : "Ordenar por fecha - reciente primero"}
            >
              <CalendarClock className="h-3.5 w-3.5" />
              {sortMode === "date" ? "Recientes primero" : "Ordenar por fecha"}
            </button>
            <button
              type="button"
              onClick={() => setSortMode((m) => (m === "name" ? "default" : "name"))}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold transition-all ${
                sortMode === "name"
                  ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
              }`}
              data-testid="sort-by-name-btn"
              title={sortMode === "name" ? "Quitar orden alfabetico" : "Ordenar A-Z por paciente"}
            >
              {sortMode === "name" ? (
                <ArrowDownAZ className="h-3.5 w-3.5" />
              ) : (
                <ListFilter className="h-3.5 w-3.5" />
              )}
              {sortMode === "name" ? "Nombre A-Z" : "Ordenar por nombre"}
            </button>
            <span className="text-xs font-medium text-slate-500" data-testid="dashboard-count">
              {visiblePrograms?.length ?? "..."} {visiblePrograms?.length === 1 ? "registro" : "registros"}
            </span>
          </div>
        </div>

        {/* Nurse filter (admin only) */}
        {user?.role === "admin" && nurseOptions.length > 0 && (
          <div className="mb-3" data-testid="nurse-filter-wrap">
            <label className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
              <Users className="h-3.5 w-3.5" /> Filtrar por enfermera
            </label>
            <Select value={nurseFilter} onValueChange={setNurseFilter}>
              <SelectTrigger className="h-11 text-base" data-testid="nurse-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="nurse-filter-all">
                  Todas las enfermeras ({programs?.length || 0})
                </SelectItem>
                {nurseOptions.map((n) => {
                  const count = programs?.filter((p) => (p.owner_name || "").trim() === n).length || 0;
                  return (
                    <SelectItem key={n} value={n} data-testid={`nurse-filter-${n.replace(/\s+/g, "-").toLowerCase()}`}>
                      {n} ({count})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {nurseFilter !== "all" && sortMode !== "name" && (
              <p className="mt-1 text-[11px] font-medium text-slate-500">
                Ordenado por fecha del turno - el mas reciente primero.
              </p>
            )}
          </div>
        )}

        {/* Filter bar (clinical priority semaforo) */}
        {programs && programs.length > 0 && (
          <div className="-mx-1 mb-4 flex gap-2 overflow-x-auto pb-1 pl-1" data-testid="priority-filter-bar">
            {FILTER_OPTIONS.map((opt) => {
              const active = filter === opt.key;
              const count = counts[opt.key] || 0;
              return (
                <button
                  key={opt.key}
                  onClick={() => setFilter(opt.key)}
                  className={`flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-all ${
                    active
                      ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                  }`}
                  data-testid={`priority-filter-${opt.key}`}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: opt.dot }}
                  />
                  {opt.label}
                  <span
                    className={`ml-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {programs === null && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        )}

        {programs && programs.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-12 text-center">
            <ClipboardList className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="font-semibold text-slate-700">Aun no hay programas</p>
            <p className="mt-1 text-sm text-slate-500">
              Crea tu primer programa de cuidados con el boton de arriba.
            </p>
          </div>
        )}

        {programs && programs.length > 0 && visiblePrograms.length === 0 && (
          <div
            className="rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-10 text-center"
            data-testid="dashboard-empty-filter"
          >
            <p className="font-semibold text-slate-700">No hay programas en esta categoria</p>
            <p className="mt-1 text-sm text-slate-500">Cambia el filtro para ver otros estados.</p>
          </div>
        )}

        <div className="space-y-3">
          {visiblePrograms?.map((p) => {
            const pr = priorities[p.id] || { level: "gray", reasons: [] };
            const cfg = LEVELS[pr.level];
            return (
              <div
                key={p.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:p-5"
                style={{ borderLeft: `4px solid ${cfg.border}` }}
                data-testid={`program-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <button
                    onClick={() => nav(`/programs/${p.id}`)}
                    className="flex-1 text-left"
                    data-testid={`program-open-${p.id}`}
                  >
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <div className="text-base font-bold text-slate-900 sm:text-lg">
                        {p.shift?.patient_name?.trim() || "Sin nombre de paciente"}
                      </div>
                      <PriorityBadge priority={pr} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 sm:text-sm">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" /> {p.shift?.date || "Sin fecha"}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {p.shift?.start_time || "--:--"}
                        {(() => {
                          const d = computeShiftDuration(p.shift);
                          return d ? ` (${d.label})` : "";
                        })()}
                      </span>
                      {user?.role === "admin" && (
                        <span className="inline-flex items-center gap-1">
                          <User2 className="h-3.5 w-3.5" /> {p.owner_name}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF6FF] px-2.5 py-1 text-xs font-semibold text-[#1E3A8A]">
                        {(p.interventions?.length || 0)} intervenciones
                      </span>
                      {pr.reasons.length > 0 && pr.level !== "green" && (
                        <span
                          className="truncate rounded-full px-2.5 py-1 text-[11px] font-medium"
                          style={{ backgroundColor: cfg.bg, color: cfg.color, maxWidth: "100%" }}
                          data-testid={`priority-reasons-${p.id}`}
                        >
                          {pr.reasons.slice(0, 2).join(" - ")}
                          {pr.reasons.length > 2 && ` +${pr.reasons.length - 2}`}
                        </span>
                      )}
                    </div>
                  </button>
                  <div className="flex flex-col gap-1.5">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-9 w-9 rounded-lg hover:bg-slate-100"
                      onClick={() => nav(`/programs/${p.id}`)}
                      data-testid={`program-edit-${p.id}`}
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4 text-slate-600" />
                    </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 rounded-lg hover:bg-slate-100"
                    onClick={() => handleDuplicate(p.id)}
                    data-testid={`program-duplicate-${p.id}`}
                    title="Duplicar"
                  >
                    <Copy className="h-4 w-4 text-slate-600" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 rounded-lg hover:bg-red-50"
                    onClick={() => setToDelete(p)}
                    data-testid={`program-delete-${p.id}`}
                    title="Eliminar"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            </div>
            );
          })}
        </div>

      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar programa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta accion no se puede deshacer. Se eliminara el programa de cuidados de{" "}
              <strong>{toDelete?.shift?.patient_name || "(sin nombre)"}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              data-testid="delete-confirm-btn"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PatientPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onConfirm={handleCreate}
        isAdmin={user?.role === "admin"}
      />
    </div>
  );
}
